import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { OrganizersAPI } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, KeyRound, Users } from "lucide-react";

const inputClass =
  "bg-[#0E0F13] border-white/10 text-white placeholder:text-zinc-600 focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37] rounded-md";

export default function AdminOrganizers() {
  const [organizers, setOrganizers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = () =>
    OrganizersAPI.list()
      .then(setOrganizers)
      .catch(() => toast.error("Impossible de charger les organisateurs"));

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password) {
      toast.error("Email et mot de passe requis");
      return;
    }
    setSaving(true);
    try {
      await OrganizersAPI.create(form);
      toast.success("Compte organisateur créé");
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la création");
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async (o) => {
    const pwd = window.prompt(`Nouveau mot de passe pour ${o.email} (min. 6 caractères) :`);
    if (!pwd) return;
    try {
      await OrganizersAPI.update(o.id, { password: pwd });
      toast.success("Mot de passe mis à jour");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la mise à jour");
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`Supprimer le compte de ${o.email} ? Ses évènements resteront en ligne.`))
      return;
    try {
      await OrganizersAPI.remove(o.id);
      toast.success("Compte supprimé");
      load();
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <div className="max-w-3xl mx-auto w-full px-4 md:px-8 py-10 md:py-16">
        <Link
          to="/admin/settings"
          data-testid="organizers-back-link"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-xs uppercase tracking-[0.25em] font-body mb-6"
        >
          <ArrowLeft className="w-3 h-3" /> Retour
        </Link>

        <h1 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none mb-2">
          Comptes <span style={{ color: "#D4AF37" }}>organisateurs</span>
        </h1>
        <p className="text-zinc-400 font-body mb-10">
          Chaque organisateur gère uniquement ses propres tournois et championnats
          (équipes, résultats, classement, photos, vidéos, sponsors, QR codes) — sans accès
          à l'administration générale ni aux évènements des autres.
        </p>

        <form
          onSubmit={create}
          className="bg-[#0A0B0E] border border-white/10 rounded-xl p-4 sm:p-5 mb-8 grid sm:grid-cols-[1fr,1fr,1fr,auto] gap-3 items-end"
          data-testid="create-organizer-form"
        >
          <div className="space-y-1.5">
            <Label className="text-overline !text-zinc-400">Nom</Label>
            <Input
              className={inputClass}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Club de Genève"
              data-testid="organizer-name-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline !text-zinc-400">Email *</Label>
            <Input
              className={inputClass}
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="organisateur@club.ch"
              data-testid="organizer-email-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline !text-zinc-400">Mot de passe *</Label>
            <Input
              className={inputClass}
              type="text"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              placeholder="min. 6 caractères"
              data-testid="organizer-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={saving}
            data-testid="create-organizer-btn"
            className="text-black rounded-md h-10 font-body font-medium hover:opacity-90"
            style={{ backgroundColor: "#D4AF37" }}
          >
            <Plus className="w-4 h-4 mr-1" /> Créer
          </Button>
        </form>

        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight">
            Organisateurs actifs
          </h2>
          <span className="text-overline" style={{ color: "#D4AF37" }}>
            {organizers.length}
          </span>
        </div>
        {organizers.length === 0 ? (
          <div
            className="border border-dashed border-white/10 rounded-xl p-8 text-center text-zinc-500 font-body text-sm"
            data-testid="organizers-empty"
          >
            <Users className="w-8 h-8 mx-auto mb-2 text-zinc-600" />
            Aucun compte organisateur pour le moment.
          </div>
        ) : (
          <div className="space-y-3">
            {organizers.map((o) => (
              <div
                key={o.id}
                className="bg-[#111216] border border-white/10 rounded-xl p-4 flex items-center gap-4"
                data-testid={`organizer-row-${o.email}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center font-display font-black text-[#D4AF37] shrink-0">
                  {(o.name || o.email)[0]?.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold uppercase tracking-tight truncate">
                    {o.name || "—"}
                  </div>
                  <div className="text-xs text-zinc-500 font-body truncate">{o.email}</div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => resetPassword(o)}
                  className="text-zinc-400 hover:text-white hover:bg-white/5"
                  data-testid={`organizer-reset-pwd-${o.email}`}
                  title="Réinitialiser le mot de passe"
                >
                  <KeyRound className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => remove(o)}
                  className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  data-testid={`organizer-delete-${o.email}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
